import { coerceIsoDate, formatISO, isValidDate } from '@fmnts/core/chronos';
import * as Decimal from '@fmnts/core/decimal';
import { CampaignForm } from '@formunauts/shared/campaigning/domain';
import { pipe } from 'effect/Function';
import * as O from 'effect/Option';
import { hasProperty } from 'effect/Predicate';

type ITransformer<TOut, TIn> = (value: TIn) => TOut;

/**
 * @param inputData The input data
 * @param fields The fields
 *
 * @returns
 * A key/value object where key represents keys of the passed `fields`
 * and value is the matching value from `inputData`.
 */
export function prepareFormData(
  inputData: CampaignForm.UnpreparedFormData,
  fields: ReadonlyArray<CampaignForm.Field>,
): CampaignForm.Payload {
  return CampaignForm.Payload(
    fields
      .filter((field) => filterField(inputData, field))
      .map((field) => ({
        [field.key]: transformFormValue(inputData[field.key], field.widget),
      }))
      .reduce((a, v) => ({ ...a, ...v }), {}),
  );
}

function filterField(
  inputData: CampaignForm.UnpreparedFormData,
  field: CampaignForm.Field,
) {
  // skip sending fields to the backend
  // - fields without a key
  // - summary fields
  // - fields that are disabled (not present in inputData)
  // - if field.attrs is undefined or read_only is true
  return (
    field.key &&
    !field.key.startsWith('summary_') &&
    field.key in inputData &&
    field.attrs?.['read_only'] !== true
  );
}

/**
 * Used to process data before sending it to the API, because most columns
 * have a not null constraint in the database
 */
export function transformFormValue(value: unknown, type: string): unknown {
  return transformerForType(type)(value);
}

function transformerForType(type: string): ITransformer<unknown, unknown> {
  switch (type) {
    case 'signaturePad':
      return transformSignature;
    case 'customDateField':
      return transformDate;
    case 'amountChoice':
    case 'multiIntervalAmountChoice':
      return transformDecimal;
    default:
      return transformNullish;
  }
}

const transformSignature: ITransformer<unknown, unknown> = (value) => {
  if (hasProperty(value, 'svg')) {
    return value.svg;
  } else {
    return '';
  }
};

const transformDate: ITransformer<string | null, unknown | string | Date> = (
  value,
) => {
  const asDate = coerceIsoDate(value);

  return isValidDate(asDate)
    ? formatISO(asDate, { representation: 'date' })
    : null;
};

const transformDecimal: ITransformer<string | null, unknown> = (value) =>
  pipe(
    Decimal.fromUnknown(value),
    O.map((d) => d.toString()),
    O.getOrNull,
  );

const transformNullish: ITransformer<unknown, unknown> = (value) => {
  if (value === null || value === undefined) {
    return '';
  } else {
    return value;
  }
};
